/* eslint-disable @typescript-eslint/naming-convention -- MUI naming*/
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { heIL } from "@mui/x-date-pickers/locales";
import { useMemo, type FC } from "react";
import "./DateAndHours.scss";

import {
  Checkbox,
  createTheme,
  Divider,
  FormControlLabel,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { isValid } from "date-fns";
import "dayjs/locale/he";
import { weekDays } from "../../../consts/consts";
import {
  REQUEST_DEFAULT_REPEAT_CONFIG,
  useRequest,
} from "../../../contexts/RequestProvider";
import { useForm } from "../../../helpers/forms/FormProvider";
import { FormTextField } from "../../form-components/form-text-field/FormTextField";
import { RepeatedEvent } from "../../repeated-event/RepeatedEvent";
import { CheckboxIcon } from "@shared/frontend/icons";
import { TIME_DESCRIPTION_MAX_LENGTH } from "@shared/helpers/constants";
import type { Opportunity } from "@entities";

export type DateEvent = (dayjs.Dayjs & { $d?: Date; }) | null;

interface DateAndHoursRangeProps {
  names: [string, string];
  past?: boolean;
  isEdit?: boolean;
  isRepeated?: boolean;
}

const theme = createTheme({
  direction: "rtl",
  palette: {
    primary: {
      main: "#05204CF2",
    },
  },
});

export const DateAndHoursRange: FC<DateAndHoursRangeProps> = ({
  names,
  past,
  isEdit = false,
  isRepeated = false,
}) => {
  const {
    setCurrentEvent: setRequestDetails,
    currentEvent: requestDetails,
    repeatConfig: {
      days: selectedDays,
      repeatType: selectedOption,
      repeatInterval: repeatedEventsNumber,
    } = REQUEST_DEFAULT_REPEAT_CONFIG!,
    resetRepeatConfig,
  } = useRequest();

  const { setTouched } = useForm<Partial<Opportunity>>();
  const markScheduleTouched = () => {
    setTouched((t) => ({ ...t, startDate: true, endDate: true }));
  };

  const requestIsRepeated = !!requestDetails.isRepeated;

  const repeatedText = useMemo(() => {
    const selectedDayNames = weekDays
      .filter(({ value }) =>
        selectedDays?.some((selected) => selected === value),
      )
      .map(({ name }) => name);

    let daysText = "";
    if (selectedDayNames.length === 1) {
      daysText = `ביום ${selectedDayNames[0]}'`;
    } else if (
      selectedDayNames.length === 5 &&
      selectedDayNames.includes("א") &&
      selectedDayNames.includes("ב") &&
      selectedDayNames.includes("ג") &&
      selectedDayNames.includes("ד") &&
      selectedDayNames.includes("ה")
    ) {
      daysText = "בימים ראשון עד חמישי";
    } else if (selectedDayNames.length === 7) {
      daysText = "בכל הימים";
    } else if (selectedDayNames.length === 2) {
      daysText = `בימים ${selectedDayNames.join("' ו- ")}'`;
    } else {
      daysText = `בימים ${selectedDayNames.join("', ")}'`;
    }

    let periodText = "";
    if (selectedOption === "WEEKS") {
      periodText =
        repeatedEventsNumber === 1 ? "שבוע" : `${repeatedEventsNumber} שבועות`;
    } else if (selectedOption === "MONTHS") {
      periodText =
        repeatedEventsNumber === 1 ? "חודש" : `${repeatedEventsNumber} חודשים`;
    }

    return `חזרה ${daysText} למשך ${periodText}`;
  }, [selectedDays, selectedOption, repeatedEventsNumber]);

  const showingRepeat =
    (!isEdit || isRepeated) &&
    requestDetails?.hasTime &&
    requestDetails?.hasStartTime;

  return (
    <Stack>
      <Stack className="date-time-container">
        <ThemeProvider theme={theme}>
          <LocalizationProvider
            adapterLocale="he"
            localeText={
              heIL.components.MuiLocalizationProvider.defaultProps.localeText
            }
            dateAdapter={AdapterDayjs}
          >
            <Stack flexDirection="row">
              <Stack
                flex={showingRepeat ? 0.68 : 1.1}
                justifyContent="center"
                className="no-time-container"
              >
                {requestDetails.hasTime && (
                  <FormControlLabel
                    sx={{ margin: 0 }}
                    slotProps={{
                      typography: {
                        sx: {
                          fontSize: "small",
                        },
                      },
                    }}
                    control={
                      <Checkbox
                        sx={{ padding: "0.5rem" }}
                        checkedIcon={
                          <CheckboxIcon
                            sx={{ width: "1vw", height: "1vw" }}
                            checked
                          />
                        }
                        icon={
                          <CheckboxIcon sx={{ width: "1vw", height: "1vw" }} />
                        }
                        onChange={(event) => {
                          markScheduleTouched();
                          const toUntil = event.target.checked;
                          setRequestDetails((prev) => ({
                            ...prev,
                            hasStartTime: !toUntil,
                            startDate: toUntil
                              ? null
                              : (prev.startDate ?? prev.endDate ?? new Date()),
                          }));
                          resetRepeatConfig();
                        }}
                      />
                    }
                    label="לביצוע עד"
                    checked={!requestDetails.hasStartTime}
                  />
                )}
                <FormControlLabel
                  sx={{ margin: 0 }}
                  slotProps={{
                    typography: {
                      sx: {
                        fontSize: "small",
                      },
                    },
                  }}
                  control={
                    <Checkbox
                      sx={{ padding: "0.5rem" }}
                      checkedIcon={
                        <CheckboxIcon
                          sx={{ width: "1vw", height: "1vw" }}
                          checked
                        />
                      }
                      icon={
                        <CheckboxIcon sx={{ width: "1vw", height: "1vw" }} />
                      }
                      onChange={(event) => {
                        markScheduleTouched();
                        setRequestDetails((prev) => ({
                          ...prev,
                          hasTime: !event.target.checked,
                          timeDescription: !event.target.checked
                            ? ""
                            : prev.timeDescription,
                          startDate: event.target.checked
                            ? null
                            : prev.startDate || new Date(),
                          endDate: event.target.checked
                            ? null
                            : prev.endDate || new Date(),
                          hasStartTime: true,
                        }));
                        resetRepeatConfig();
                      }}
                    />
                  }
                  label="ללא זמן מוגדר"
                  checked={!requestDetails.hasTime}
                />
              </Stack>
              <Divider orientation="vertical" sx={{ margin: "0 0.5vw" }} />
              <Stack
                flex={showingRepeat ? 1.85 : 3}
                alignItems="center"
                direction="row"
                gap={1}
              >
                {requestDetails.hasTime && (
                  <>
                    <Stack gap={0} flex={1.1} className="field-container">
                      <label htmlFor="date-picker" className="field-label">
                        {requestDetails.hasStartTime ? "בתאריך" : "עד תאריך"}
                      </label>
                      <DatePicker
                        format="DD/MM/YYYY"
                        slotProps={{
                          leftArrowIcon: {
                            sx: { transform: "rotate(180deg)" },
                          },
                          rightArrowIcon: {
                            sx: { transform: "rotate(180deg)" },
                          },
                          textField: {
                            inputProps: {
                              id: "date-picker",
                              style: { cursor: "pointer" },
                            },
                            InputProps: { className: "field date-field" },
                            FormHelperTextProps: { className: "helper-text" },
                          },
                          openPickerIcon: {
                            fontSize: "small",
                          },
                          openPickerButton: {
                            sx: {
                              padding: "0.25rem",
                            },
                          },
                          desktopPaper: {
                            className: "desktop-event-popup-container",
                          },
                          mobilePaper: {
                            className: "mobile-event-popup-container",
                          },
                        }}
                        slots={{
                          openPickerIcon: CalendarTodayRoundedIcon,
                        }}
                        disablePast={!past}
                        value={
                          dayjs(
                            requestDetails.hasStartTime
                              ? requestDetails.startDate
                              : requestDetails.endDate,
                          ) || new Date()
                        }
                        onChange={(event: DateEvent) => {
                          if (!isValid(event?.toDate())) return;
                          markScheduleTouched();
                          const date = new Date(event?.$d || "");
                          if (requestDetails.hasStartTime) {
                            setRequestDetails((prev) => ({
                              ...prev,
                              startDate: date,
                              endDate: date,
                            }));
                          } else {
                            setRequestDetails((prev) => ({
                              ...prev,
                              endDate: date,
                            }));
                          }
                        }}
                      />
                    </Stack>
                    {requestDetails.hasStartTime && (
                      <Stack gap={0} flex={0.8} className="field-container">
                        <label
                          htmlFor="start-time-picker"
                          className="field-label"
                        >
                          משעה
                        </label>
                        <TimePicker
                          slotProps={{
                            textField: {
                              inputProps: {
                                id: "start-time-picker",
                                style: { cursor: "pointer" },
                              },
                              InputProps: { className: "field date-field" },
                              FormHelperTextProps: { className: "helper-text" },
                            },
                            openPickerIcon: {
                              fontSize: "small",
                            },
                            openPickerButton: {
                              sx: {
                                padding: "0.25rem",
                              },
                            },
                            desktopPaper: {
                              sx: {
                                ".MuiMultiSectionDigitalClock-root": {
                                  height: "13vh",
                                  ".MuiMultiSectionDigitalClockSection-root": {
                                    width: "8.2vw",
                                    maxHeight: "13vh",
                                  },
                                },
                              },
                            },
                            digitalClockSectionItem: {
                              sx: {
                                width: "7vw",
                              },
                            },
                            digitalClockItem: {
                              sx: {
                                paddingRight: "1rem",
                              },
                            },
                          }}
                          timeSteps={{ minutes: 15 }}
                          format="HH:mm"
                          value={dayjs(
                            requestDetails.startDate
                              ? new Date(requestDetails.startDate)
                              : new Date(new Date().setMinutes(0)).setHours(0),
                          )}
                          disablePast={
                            requestDetails.startDate?.getDate() ===
                              new Date().getDate()
                              ? !past
                              : false
                          }
                          onChange={(event: DateEvent) => {
                            if (!isValid(event?.toDate())) return;
                            markScheduleTouched();
                            const date = requestDetails.startDate
                              ? new Date(requestDetails.startDate || "")
                              : new Date();
                            date.setMinutes(
                              new Date(event?.$d || "").getMinutes(),
                            );
                            date.setHours(new Date(event?.$d || "").getHours());
                            date.setSeconds(0);
                            setRequestDetails((prev) => ({
                              ...prev,
                              startDate: date,
                            }));
                          }}
                        />
                      </Stack>
                    )}
                  </>
                )}
                {requestDetails.hasTime && (
                  <>
                    <Stack gap={0} flex={0.8} className="field-container">
                      <label htmlFor="end-time-picker" className="field-label">
                        {requestDetails.hasStartTime ? "עד שעה" : "בשעה"}
                      </label>
                      <TimePicker
                        slotProps={{
                          textField: {
                            inputProps: {
                              id: "start-time-picker",
                              style: { cursor: "pointer" },
                            },
                            InputProps: { className: "field date-field" },
                            FormHelperTextProps: { className: "helper-text" },
                          },
                          openPickerIcon: {
                            fontSize: "small",
                          },
                          openPickerButton: {
                            sx: {
                              padding: "0.25rem",
                            },
                          },
                          desktopPaper: {
                            sx: {
                              ".MuiMultiSectionDigitalClock-root": {
                                height: "13vh",
                                ".MuiMultiSectionDigitalClockSection-root": {
                                  width: "8.2vw",
                                  maxHeight: "13vh",
                                },
                              },
                            },
                          },
                          digitalClockSectionItem: {
                            sx: {
                              width: "7vw",
                            },
                          },
                          digitalClockItem: {
                            sx: {
                              paddingRight: "1rem",
                            },
                          },
                        }}
                        timeSteps={{ minutes: 10 }}
                        format="HH:mm"
                        disablePast={
                          requestDetails.endDate?.getDate() ===
                            new Date().getDate()
                            ? !past
                            : false
                        }
                        minTime={
                          requestDetails.hasStartTime
                            ? dayjs(requestDetails.startDate || new Date())
                            : undefined
                        }
                        value={dayjs(
                          requestDetails.endDate
                            ? new Date(requestDetails.endDate)
                            : new Date(new Date().setMinutes(0)).setHours(0),
                        )}
                        onChange={(event: DateEvent) => {
                          if (!isValid(event?.$d)) return;
                          markScheduleTouched();
                          const date = requestDetails.endDate
                            ? new Date(requestDetails.endDate || "")
                            : requestDetails.startDate
                              ? new Date(requestDetails.startDate || "")
                              : new Date();
                          date.setMinutes(
                            new Date(new Date(event?.$d || "")).getMinutes(),
                          );
                          date.setHours(
                            new Date(new Date(event?.$d || "")).getHours(),
                          );
                          date.setSeconds(0);
                          setRequestDetails((prev) => ({
                            ...prev,
                            endDate: date,
                          }));
                        }}
                      />
                    </Stack>
                  </>
                )}
                {!requestDetails.hasTime && (
                  <FormTextField
                    label=""
                    name="timeDescription"
                    containerProps={{ className: "time-description-container" }}
                    placeholder="תיאור זמן : פעם בשבוע , כל יום ..."
                    value={requestDetails.timeDescription}
                    onChange={(e) =>
                      setRequestDetails((prev) => ({
                        ...prev,
                        timeDescription: e.target.value.slice(
                          0,
                          TIME_DESCRIPTION_MAX_LENGTH,
                        ),
                      }))
                    }
                    inputProps={{ maxLength: TIME_DESCRIPTION_MAX_LENGTH }}
                  />
                )}
              </Stack>
            </Stack>
            <Stack
              flex={0.5}
              justifyContent="center"
              className="repeat-container"
            >
              {showingRepeat && <RepeatedEvent />}
            </Stack>
          </LocalizationProvider>
        </ThemeProvider>
      </Stack>
      {requestIsRepeated &&
        (selectedOption === "WEEKS" || selectedOption === "MONTHS") && (
          <Typography color="primary" fontSize="small" fontWeight="600">
            {repeatedText}
          </Typography>
        )}
    </Stack>
  );
};
/* eslint-enable @typescript-eslint/naming-convention -- MUI naming*/
